import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  MultiStepApplicationProvider,
  useMultiStepApplication,
} from '../../../store/Context/MultiStepApplicationsProvider';
import { notifyUser } from '../../../store/Toast';
import { JsonForm } from '../../UI/JsonForm';
import Button from '../../UI/Button';
import { ApplicationFormStep } from './types';
import ApplicationFormSections from './ApplicationFormSections';
import { PPSignedAssetResponse } from '../../../api/Rover/partnerProposal';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  SignAssetFn,
  UploadFnReturn,
  useFileUpload,
} from '../../../hooks/useFileUpload';
import { SignDeveloperAssetPayload } from '../../../types/Shared';
import { OptionalObjectSchema } from 'yup/lib/object';
import { generateInitialValues } from './initialValuesGenerator';
import { useEffect, useState } from 'react';
export interface ApplicationFormBaseProps {
  steps: ApplicationFormStep[];
  title: string;
  logo?: string;
  initialValues: Record<string, any>;
  validationSchema?: OptionalObjectSchema<any, any, any>[];
  signAsset: SignAssetFn;
  onSubmit: (values: any) => Promise<any>;
  onSuccess?: (
    data: unknown,
    variables: void,
    context: unknown | undefined
  ) => Promise<unknown> | void;
  onError?: (
    error: unknown,
    variables: void,
    context: unknown | undefined
  ) => Promise<unknown> | void;
}

const ApplicationFormBase = (props: ApplicationFormBaseProps) => {
  const {
    steps,
    initialValues,
    title,
    logo,
    validationSchema,
    onSubmit,
    onSuccess,
    onError,
    signAsset,
  } = props;
  const dispatch = useDispatch();
  const [uploadError, setUploadError] = useState<string>('');
  const {
    currentStep,
    setCurrentStep,
    completedSteps,
    updateData,
    data,
    setFormJSON,
  } = useMultiStepApplication();

  useEffect(() => {
    if (steps) {
      setFormJSON(steps);
    }
    return () => {
      setFormJSON([]);
    };
  }, [steps]);

  const { upload } = useFileUpload({
    signAsset,
  });

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const formMutation = useMutation((payload: any) => onSubmit(payload), {
    onSuccess: (...params) => {
      if (onSuccess) {
        onSuccess(...params);
      }
    },
    onError: (...params) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `We are unable to submit your form at this time. Please try again later.`,
          toastType: 'failed',
        })
      );
      if (onError) {
        onError(...params);
      }
    },
  });
  const increaseStep = () => {
    if (!isLast) {
      setCurrentStep((prev) => prev + 1);
    }
  };
  const decreaseStep = () => {
    if (!isFirst) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: SignDeveloperAssetPayload[];
      files: File[];
      formState: any;
    }) => upload(payload),
    {
      onSuccess: ({ sign, files }: UploadFnReturn, { formState }) => {
        let signRes: PPSignedAssetResponse[] = [];
        if (Array.isArray(sign)) {
          signRes = [...sign];
        } else {
          signRes = [{ ...sign }];
        }

        handleNext({
          ...formState,
          uploadedFiles: {
            ...data.uploadedFiles,
            [currentStep]: signRes.map((sig, index) => ({
              name: files[index].name,
              path: sig.path,
              key: sig.key,
              type: files[index].type,
              field: sig.field,
              description: sig.field,
            })),
          },
        });
      },
      onError: (error: any) => {
        setUploadError('Failed to upload Document');
      },
    }
  );

  const handleNext = (values: any) => {
    updateData(values);
    increaseStep();
  };

  const onSubmitHandler = async (
    values: Record<string, any> & {
      selectedFiles: { [currentStep: string]: { [id: string]: File[] } };
    }
  ) => {
    setUploadError('');
    if (!isLast) {
      const selectedFiles =
        values.selectedFiles && values.selectedFiles[currentStep];
      if (selectedFiles) {
        const selectedFilesKeys = Object.keys(selectedFiles);
        const { allFilesInASection, allFilesInSectionSignPayload } =
          selectedFilesKeys.reduce<{
            allFilesInASection: File[];
            allFilesInSectionSignPayload: SignDeveloperAssetPayload[];
          }>(
            (acc, currentKey) => {
              const { files, sign } = selectedFiles[currentKey].reduce<{
                files: File[];
                sign: SignDeveloperAssetPayload[];
              }>(
                (innerAcc, currentFile) => {
                  const signPayload: SignDeveloperAssetPayload = {
                    fileName: currentFile.name,
                    fileSize: currentFile.size,
                    fileType: currentFile.type,
                    field: currentKey,
                  };
                  innerAcc = {
                    ...innerAcc,
                    files: [...innerAcc.files, currentFile],
                    sign: [...innerAcc.sign, signPayload],
                  };
                  return innerAcc;
                },
                { files: [], sign: [] }
              );
              acc = {
                ...acc,
                allFilesInASection: [...acc.allFilesInASection, ...files],
                allFilesInSectionSignPayload: [
                  ...acc.allFilesInSectionSignPayload,
                  ...sign,
                ],
              };
              return acc;
            },
            { allFilesInASection: [], allFilesInSectionSignPayload: [] }
          );
        uploadFile.mutate({
          signAssetPayload: allFilesInSectionSignPayload,
          files: allFilesInASection,
          formState: values,
        });
      } else {
        handleNext({
          ...values,
          uploadedFiles: { ...data.uploadedFiles, [currentStep]: [] },
        });
      }
    } else {
      const { selectedFiles, ...rest } = values;
      const files = Object.keys(data.uploadedFiles).reduce<any[]>(
        (acc, key) => {
          acc = [...acc, ...data.uploadedFiles[key]];
          return acc;
        },
        []
      );
      const payload = { formData: rest, files };
      formMutation.mutate(payload);
    }
  };
  const currentFormStep = steps[currentStep];
  return (
    <>
      <div className='container mx-auto'>
        <h2>{title}</h2>
        <div className='lg:grid lg:grid-cols-12 w-full mb-10'>
          <aside className='py-6 px-2 sm:px-6 lg:col-span-3 bg-white rounded-md'>
            {logo && (
              <img
                src={logo}
                alt={`${title} logo`}
                className='h-14 xl:h-20 object-fit'
              />
            )}
            <nav className='space-y-1 py-5'>
              {steps.map(({ title, icon: Icon }, index) => (
                <button
                  key={index}
                  disabled={!completedSteps.includes(index)}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentStep(index);
                  }}
                  className={`${
                    currentStep === index
                      ? 'bg-green-50 text-green-500'
                      : 'text-neutralColor-normal hover:text-green-500 hover:bg-green-50'
                  } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
                >
                  <Icon className='group-hover:text-green-500 flex-shrink-0 -ml-1 mr-3 h-6 w-6' />
                  {title}
                </button>
              ))}
            </nav>
          </aside>
          <div className='space-y-6 sm:px-6 lg:px-4 lg:col-span-9 overflow-y-scroll no-scrollbar bg-gray-50'>
            <JsonForm
              onSubmit={onSubmitHandler}
              hookFormProviderProps={{
                defaultValues: initialValues,
                mode: 'all',
                resolver:
                  validationSchema &&
                  validationSchema[currentStep] &&
                  yupResolver(validationSchema[currentStep]),
              }}
              formClassName='p-4 bg-gray-50'
            >
              {currentFormStep && (
                <ApplicationFormSections
                  step={currentFormStep}
                  currentStep={currentStep}
                />
              )}
              {uploadError && (
                <p className='text-tertiaryColor-normal text-md col-span-6'>
                  {uploadError}
                </p>
              )}
              <div className='col-span-12 flex justify-end space-x-4 py-4'>
                {!isFirst && (
                  <Button
                    label='Previous'
                    btnStyle='btn-neutral'
                    type='button'
                    disabled={
                      isFirst || formMutation.isLoading || uploadFile.isLoading
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      decreaseStep();
                    }}
                  />
                )}
                <Button
                  label={`${
                    formMutation.isLoading || uploadFile.isLoading
                      ? 'Please Wait...'
                      : isLast
                      ? 'Submit'
                      : 'Next'
                  }`}
                  btnStyle='btn-primary'
                  disabled={formMutation.isLoading || uploadFile.isLoading}
                />
              </div>
            </JsonForm>
          </div>
        </div>
      </div>
    </>
  );
};

const ApplicationForm = (
  props: Omit<ApplicationFormBaseProps, 'initialValues'>
) => {
  const { steps } = props;
  const initialValues = generateInitialValues(steps);
  return (
    <MultiStepApplicationProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} initialValues={initialValues} />
    </MultiStepApplicationProvider>
  );
};
export default ApplicationForm;
